'use client';

/**
 * Discover's data layer: a module-level stale-while-revalidate cache.
 *
 * The surface was fetching everything on MOUNT, so every open paid three to
 * four round trips while bands popped in one by one (the user read it as
 * "takes long to open and the layout changes during load"). Now:
 *
 *  - `peekJson` returns the last known payload SYNCHRONOUSLY, so a reopen
 *    renders complete on the first frame;
 *  - `cachedJson` revalidates in the background (TTL below) and dedupes
 *    in-flight requests, so N consumers of one URL cost one fetch;
 *  - `prefetchDiscover` warms the four first-paint URLs from the shell as
 *    soon as the workspace mounts, so even the FIRST open is warm.
 *
 * Cache is per-tab and keyed by URL. Auth-sensitive URLs (recent files) are
 * in here too — the cache dies with the tab, same lifetime as the session
 * cookie's tab-scoped use, and a 401 payload is simply not stored.
 */

type Entry = {
  data: unknown | null;
  at: number;
  inflight: Promise<unknown | null> | null;
};

const cache = new Map<string, Entry>();

const DEFAULT_TTL = 60_000;

function entryFor(url: string): Entry {
  let e = cache.get(url);
  if (!e) {
    e = { data: null, at: 0, inflight: null };
    cache.set(url, e);
  }
  return e;
}

/** Last known payload, synchronously — null when never fetched. */
export function peekJson<T>(url: string): T | null {
  return (cache.get(url)?.data as T) ?? null;
}

/**
 * Resolve the URL's payload: instantly from cache when fresh, otherwise
 * fetching (deduped). On failure resolves null and keeps any stale data.
 */
export function cachedJson<T>(url: string, ttl = DEFAULT_TTL): Promise<T | null> {
  const e = entryFor(url);
  if (e.data !== null && Date.now() - e.at < ttl) {
    return Promise.resolve(e.data as T);
  }
  if (e.inflight) return e.inflight as Promise<T | null>;
  e.inflight = fetch(url, { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (data !== null) {
        e.data = data;
        e.at = Date.now();
      }
      return data as T | null;
    })
    .catch(() => null)
    .finally(() => {
      e.inflight = null;
    });
  return e.inflight as Promise<T | null>;
}

/** The URLs behind Discover's first paint. Recent is auth-scoped — for an
 * anonymous tab the request 401s and simply is not stored. */
export const DISCOVER_RECENT_URL =
  '/api/content?owner=me&sort=recent&excludeBundleFiles=1&limit=8&page=1';

/** My Apps pages in 60s: one page already covers all but the largest shelves. */
export const MINE_PAGE = 60;

/**
 * The My Apps shelf query. Lives here, beside the other Discover URLs, so the
 * one property that makes it a shelf of APPS is stated once and testable.
 *
 * `excludeBundleFiles=1` is that property. Without it the listing also returns
 * the FROZEN release snapshot rows a publish leaves behind, one per publish or
 * reseed of the same app. On a mature account those outnumber the real apps two
 * to one (the four office apps each appeared seven times over), so with the
 * page limit in play they pushed genuine apps clean off the shelf.
 *
 * "Nested inside a bundle folder" is NOT what distinguishes them, though the
 * flag was implemented that way until 2026-09-07: an app's own catalog row is
 * parented inside its bundle folder as well, so the parent test emptied this
 * shelf completely for anyone who had authored apps of their own. The server
 * now names the snapshots by the versions table instead. See
 * `app/api/content/route.ts`.
 *
 * `owner=me` is resolved from the SESSION by `GET /api/content` — there is no
 * owner id on the query string and none may be added. That is what makes the
 * shelf safe to reuse on any surface: a caller cannot ask for somebody else's
 * private apps by lying about who it is.
 *
 * `limit` defaults to this surface's own page size. The Discover hub's Mine
 * scope (see `components/discover/use-discover.ts`, 2026-09-06) pages at the
 * feed's 20 instead of forking a second copy of the query.
 */
export function mineShelfUrl(page: number, query?: string, limit: number = MINE_PAGE): string {
  const params = new URLSearchParams({
    type: 'widget',
    owner: 'me',
    excludeBundleFiles: '1',
    limit: String(limit),
    page: String(page),
    sort: 'newest',
  });
  if (query) params.set('q', query);
  return `/api/content?${params.toString()}`;
}

/**
 * The other half of the My Apps shelf: which of the caller's own apps have a
 * marketplace listing behind them. Session-scoped server-side (owner rows on
 * `channel_permissions`), so like `mineShelfUrl` it carries no identity on the
 * query string.
 *
 * Paired with the shelf everywhere the shelf is used, because "is this app
 * published" is not answerable from the content row alone: a draft and a live
 * app are the same kind of row, they differ only by having a listing.
 */
export const MY_LISTINGS_URL = '/api/marketplace/my-listings';

/** One row of `MY_LISTINGS_URL`'s `listings` map, keyed by app (content) id. */
export type MyListing = {
  slug: string;
  verified?: boolean;
  listed?: boolean;
  installCount?: number | null;
};

export const DISCOVER_PREFETCH_URLS = [
  '/api/marketplace/channels?root=apps&limit=24&offset=0',
  '/api/marketplace/channels?root=agents&limit=24&offset=0',
  '/api/workspaces/starters',
  // The REAL feed, same corpus as the Feed panel's Activity tab (hot sort:
  // announcements, tutorials, media posts) — NOT content_type=widget, which
  // returns the app release stubs and made the column a wall of app names.
  // 12 fetched; the column SLICES to match the catalog's computed height
  // (D2), so tall screens show more without a second fetch.
  '/api/posts?sort=hot&limit=12',
  DISCOVER_RECENT_URL,
] as const;

let prefetched = false;

/** Warm the first-paint payloads once per tab. Cheap to call repeatedly. */
export function prefetchDiscover(): void {
  if (prefetched || typeof window === 'undefined') return;
  prefetched = true;
  const warm = () => {
    for (const url of DISCOVER_PREFETCH_URLS) void cachedJson(url);
  };
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(warm);
  } else {
    setTimeout(warm, 400);
  }
}
